import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Key, Trash, Copy, Plus, ShareNetwork } from "@phosphor-icons/react";

export default function ApiKeys() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null); // full key shown once
  const [webhook, setWebhook] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/auth/api-keys").then((r) => setKeys(r.data)).catch(() => toast.error("Accès refusé")).finally(() => setLoading(false));
  useEffect(() => { load(); if (isAdmin) api.get("/settings/contact-webhook").then((r) => setWebhook(r.data.url || "")).catch(() => {}); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Donnez un nom à la clé");
    try { const { data } = await api.post("/auth/api-keys", { name: name.trim() }); setCreated(data); setName(""); load(); }
    catch { toast.error("Erreur"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Révoquer cette clé ? Les scripts l'utilisant cesseront de fonctionner.")) return;
    try { await api.delete(`/auth/api-keys/${id}`); toast.success("Clé révoquée"); load(); } catch { toast.error("Erreur"); }
  };
  const saveWebhook = async () => {
    try { await api.put("/settings/contact-webhook", { url: webhook.trim() }); toast.success("Webhook enregistré"); } catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-8" data-testid="apikeys-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><Key size={22} weight="fill" className="text-white" /></div>
        <div><h1 className="font-head font-bold text-2xl">{isAdmin ? "Clés API & Webhook" : "Ma clé API"}</h1>
          <p className="text-sm text-muted-foreground">{isAdmin
            ? "Pour les scripts d'outreach externes (API Contacts). Sans expiration, révocables."
            : "Générez votre clé personnelle pour vos scripts d'outreach. Les contacts créés via votre clé vous sont automatiquement assignés."}</p></div>
      </div>

      <div className="bg-card border border-border p-5 space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="overline text-muted-foreground">Nom de la clé</label>
            <input data-testid="apikey-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Script outreach Clarissa" className="block w-full border border-border px-3 py-2 text-sm mt-1 outline-none" />
          </div>
          <button data-testid="apikey-create" onClick={create} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold aeliam-btn-primary"><Plus size={16} /> Générer</button>
        </div>
        {created && (
          <div className="border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="apikey-created">
            <div className="text-xs text-emerald-300 font-semibold mb-1">Clé « {created.name} » — copiez-la maintenant, elle ne sera plus affichée :</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-card border border-border px-2 py-1.5 break-all" data-testid="apikey-value">{created.key}</code>
              <button onClick={() => { navigator.clipboard.writeText(created.key); toast.success("Copié"); }} className="p-2 border border-border hover:bg-secondary"><Copy size={16} /></button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border overline text-muted-foreground">{isAdmin ? "Clés actives" : "Mes clés"}</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left"><tr className="overline text-muted-foreground">
            <th className="px-4 py-2">Nom</th><th className="px-4 py-2">Préfixe</th><th className="px-4 py-2">Dernière utilisation</th><th className="px-4 py-2">Statut</th><th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Chargement…</td></tr>
              : keys.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Aucune clé.</td></tr>
              : keys.map((k) => (
                <tr key={k.id} className="border-t border-border" data-testid={`apikey-row-${k.id}`}>
                  <td className="px-4 py-2 font-medium">{k.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{k.key_prefix}…</td>
                  <td className="px-4 py-2 text-muted-foreground">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("fr-FR") : "Jamais"}</td>
                  <td className="px-4 py-2">{k.revoked ? <span className="text-xs text-red-400 font-semibold">Révoquée</span> : <span className="text-xs text-emerald-400 font-semibold">Active</span>}</td>
                  <td className="px-4 py-2 text-right">{!k.revoked && <button data-testid={`apikey-revoke-${k.id}`} onClick={() => revoke(k.id)} className="p-1.5 text-red-500 hover:text-red-700"><Trash size={16} /></button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {isAdmin && (
      <div className="bg-card border border-border p-5 space-y-3">
        <div className="flex items-center gap-2"><ShareNetwork size={18} className="text-indigo-400" /><span className="font-head font-bold">Webhook sortant (optionnel)</span></div>
        <p className="text-sm text-muted-foreground">URL appelée en POST à chaque création/mise à jour de contact (synchro app → vos scripts, sans polling).</p>
        <div className="flex items-end gap-3">
          <input data-testid="webhook-url" value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://votre-domaine/webhooks/contacts" className="flex-1 border border-border px-3 py-2 text-sm outline-none" />
          <button data-testid="webhook-save" onClick={saveWebhook} className="px-4 py-2 text-sm font-semibold border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 transition-colors">Enregistrer</button>
        </div>
      </div>
      )}
    </div>
  );
}
